import { useFormik } from 'formik';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getTanSpvPayout, postSpvTan } from 'src/api/stoApi';
import { CurrencyFieldText } from 'src/components/mui-overrides/Currency';
import { DefaultTextField } from 'src/components/mui-overrides/Textfield';
import { authStore } from 'src/stores/AuthStore';
import { awaitUtil } from 'src/util/utils';
import { number, object, string } from 'yup';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import LoadingButton from '@mui/lab/LoadingButton';
import { IconButton, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';

interface Props {
  open: boolean;
  handleClose: () => void;
  availableFunds: string;
  spvId: string;
  handleCloseAfterSuccess: () => void;
}

export interface EarningPeriodValues {
  smsTan: string;
}

export const WithdrawalTanModal: FC<Props> = ({ open, handleClose, availableFunds, spvId, handleCloseAfterSuccess }) => {
  const { t } = useTranslation();
  const phone = authStore.phone;
  const [amount, setAmount] = useState<string>('');
  const [amountError, setAmountError] = useState<string | boolean>(false);
  const [amountErrorText, setAmountErrorText] = useState<string>('');
  const [hasErrors, setHasErrors] = useState<string | boolean>(false);
  const [modalSteps, setModalSteps] = useState<number>(0);
  const [payoutId, setPayoutId] = useState<string>('');
  const [validatingAmount, setValidatingAmount] = useState<boolean>(false);

  const validationSchemaAmount = object({
    amount: number()
      .min(0, 'Withdrawal can not be negative.')
      .max(Number(availableFunds), 'You do not have sufficient funds on the account.')
      .required('Rent Surplus is required'),
  });
  const amountForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      amount: availableFunds,
    },
    validationSchema: validationSchemaAmount,
    validateOnMount: true,
    onSubmit: async (val) => {
      setAmount(val.amount);
      const [response, error] = await awaitUtil(getTanSpvPayout(spvId, val.amount));
      if (response) {
        setModalSteps(1);
        setValidatingAmount(false);
        setPayoutId(response.data.data);
      }
      if (error) {
        setAmountError(true);
        setAmountErrorText(error.message);
        setValidatingAmount(false);
      }
    },
  });

  const sendAmountToApi = async () => {
    let errors = false;
    await amountForm.validateForm();
    if (Object.keys(amountForm.errors).length > 0) {
      await amountForm.setTouched({ ...amountForm.touched, ...(amountForm.errors as any) });
    }
    // check if any form has errors
    if (amountForm.errors.amount) {
      errors = true;
      setHasErrors(true);
    }
    setHasErrors(hasErrors);
    if (!errors) {
      setValidatingAmount(true);
      await amountForm.submitForm();
    }
  };

  const [tanError, setTanError] = useState<string | boolean>(false);
  const [tanErrorText, setTanErrorText] = useState<string>('');
  const [verifyingTan, setVerifyingTan] = useState<boolean>(false);
  const validationSchemaTan = object({
    smsTan: string().min(6, 'Please enter your 6 digit sms tan').required('SMS Tan is required'),
  });

  const tanForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      smsTan: '',
    },
    validationSchema: validationSchemaTan,
    validateOnMount: true,
    onSubmit: async (val) => {
      const [response, error] = await awaitUtil(postSpvTan(payoutId, val.smsTan));
      if (response) {
        setModalSteps(2);
        setVerifyingTan(false);
      }
      if (error) {
        setTanError(true);
        setTanErrorText(error.message);
        setVerifyingTan(false);
      }
    },
  });

  const sendTan = async () => {
    let errors = false;
    await tanForm.validateForm();
    if (Object.keys(tanForm.errors).length > 0) {
      await tanForm.setTouched({ ...tanForm.touched, ...(tanForm.errors as any) });
    }
    // check if any form has errors
    if (tanForm.errors.smsTan) {
      errors = true;
      setHasErrors(true);
    }
    setHasErrors(hasErrors);
    if (!errors) {
      setVerifyingTan(true);
      await tanForm.submitForm();
    }
  };

  const resetForm = () => {
    amountForm.resetForm();
    tanForm.resetForm();
    setHasErrors(false);
    setModalSteps(0);
    setPayoutId('');
  };

  return (
    <div>
      {/* First Screen, select amount */}
      {modalSteps === 0 && (
        <Dialog open={open} onClose={handleClose} fullWidth>
          <DialogTitle>
            <Grid container>
              <Typography flexGrow={1} variant="h6">
                {t('payment.account.modal.withdrawal')}
              </Typography>
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Grid>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>{t('payment.account.modal.availableFunds', { availableFunds })}</DialogContentText>
            <form onSubmit={amountForm.handleSubmit}>
              <CurrencyFieldText
                label={t('payment.account.modal.form.placeholder.amount')}
                fieldName="amount"
                formik={amountForm}
                currency="€"
              />
            </form>
            {hasErrors && <Typography color="red">{t('payment.account.modal.errors')}</Typography>}
            {amountError && (
              <Typography color="red">
                {t('payment.account.modal.errors')}
                {amountErrorText}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary">
              {t('payment.account.modal.cancel')}
            </Button>
            <LoadingButton
              variant="contained"
              onClick={sendAmountToApi}
              loading={validatingAmount}
              disabled={Boolean(amountForm.errors.amount)}
            >
              {t('payment.account.modal.confirmAmountWithTan')}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      )}

      {/* Second Screen, before tan is validated */}
      {modalSteps === 1 && (
        <Dialog open={open} onClose={handleClose} fullWidth>
          <DialogTitle>
            <Grid container>
              <Typography flexGrow={1} variant="h6">
                {t('payment.account.modal.withdrawal')}
              </Typography>
              <IconButton
                onClick={() => {
                  resetForm();
                  handleClose();
                }}
              >
                <CloseIcon />
              </IconButton>
            </Grid>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>{t('payment.account.modal.confirmTanDisclaimer', { phone })}</DialogContentText>
            <DialogContentText>{t('payment.account.modal.confirmTanAmount', { amount })}</DialogContentText>
            <form onSubmit={tanForm.handleSubmit}>
              <DefaultTextField label="SMS Tan" fieldName="smsTan" formik={tanForm} autocomplete="one-time-code" />
            </form>
            {tanError && (
              <Typography color="red">
                {t('payment.account.modal.ErrorSMSTan')} {tanErrorText}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => {
                resetForm();
                handleClose();
              }}
            >
              {t('payment.account.modal.cancel')}
            </Button>
            <LoadingButton variant="contained" loading={verifyingTan} onClick={sendTan} disabled={Boolean(tanForm.errors.smsTan)}>
              {t('payment.account.modal.validate')}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      )}
      {/* Second Screen, after tan is validated */}
      {modalSteps === 2 && (
        <Dialog
          open={open}
          onClose={() => {
            handleCloseAfterSuccess();
          }}
          fullWidth
        >
          <DialogTitle>
            <Grid container>
              <Typography flexGrow={1} variant="h6">
                {t('payment.account.modal.titleSuccess')}
              </Typography>
              <IconButton
                onClick={() => {
                  resetForm();
                  handleCloseAfterSuccess();
                }}
              >
                <CloseIcon />
              </IconButton>
            </Grid>
          </DialogTitle>
          <DialogContent
            style={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <CheckCircleOutlineIcon style={{ fontSize: '120px' }} color="primary" />
            <DialogContentText>{t('payment.account.modal.success')}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              onClick={() => {
                resetForm();
                handleCloseAfterSuccess();
              }}
            >
              {t('payment.account.modal.closeModal')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};
